import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Users,
  Building2,
  ClipboardList,
  TrendingUp,
  TrendingDown,
  Eye,
  UserPlus,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowLeft,
  BarChart3,
  Activity,
  Loader
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatRelativeTime } from '../utils/helpers';
import dashboardService from '../services/dashboardService';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import './DashboardPage.css';

const DashboardPage = () => {
  const { t } = useTranslation();
  const { admin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dashboardService.getStats();
      if (response.success) {
        setDashboardData(response.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const stats = useMemo(() => [
    {
      icon: Users,
      value: dashboardData?.stats?.totalUsers || 0,
      label: t('dashboard.totalUsers'),
      trend: `${dashboardData?.changes?.users >= 0 ? '+' : ''}${dashboardData?.changes?.users || 0}%`,
      positive: (dashboardData?.changes?.users || 0) >= 0,
      color: '#1a5f4a'
    },
    {
      icon: Building2,
      value: dashboardData?.stats?.totalOffers || 0,
      label: t('dashboard.totalOffers'),
      trend: `${dashboardData?.changes?.offers >= 0 ? '+' : ''}${dashboardData?.changes?.offers || 0}%`,
      positive: (dashboardData?.changes?.offers || 0) >= 0,
      color: '#c4a35a'
    },
    {
      icon: ClipboardList,
      value: dashboardData?.stats?.totalRequests || 0,
      label: t('dashboard.totalRequests'),
      trend: `${dashboardData?.changes?.requests >= 0 ? '+' : ''}${dashboardData?.changes?.requests || 0}%`,
      positive: (dashboardData?.changes?.requests || 0) >= 0,
      color: '#3b82f6'
    },
    {
      icon: Eye,
      value: dashboardData?.stats?.activeUsers || 0,
      label: t('dashboard.activeUsers', 'المستخدمين النشطين'),
      trend: '+0%',
      positive: true,
      color: '#10b981'
    }
  ], [t, dashboardData]);

  const chartData = useMemo(() => 
    dashboardData?.chartData || [], 
    [dashboardData]
  );

  // Map icon types from API to actual icons
  const iconMap = useMemo(() => ({
    UserPlus: UserPlus,
    Building2: Building2,
    ClipboardList: ClipboardList,
    CheckCircle: CheckCircle,
    AlertCircle: AlertCircle
  }), []);

  const recentActivities = useMemo(() => 
    (dashboardData?.recentActivities || []).map(activity => ({
      ...activity,
      icon: iconMap[activity.iconType] || Activity,
      time: new Date(activity.time)
    })), 
    [dashboardData, iconMap]
  );

  const pendingItems = useMemo(() => ({
    offers: dashboardData?.stats?.pendingOffers || 0,
    users: 0,
    reports: dashboardData?.alerts?.length || 0
  }), [dashboardData]);

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Loader className="spin" size={40} />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="page-header">
        <div className="header-title">
          <BarChart3 size={28} />
          <div>
            <h1>{t('dashboard.title')}</h1>
            <p className="welcome-text">
              {t('dashboard.welcome')}, {admin?.name}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="stat-card">
              <div
                className="stat-icon"
                style={{ background: `${stat.color}15` }}
              >
                <Icon size={26} style={{ color: stat.color }} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stat.value.toLocaleString()}</div>
                <div className="stat-label">{stat.label}</div>
                <div className={`stat-trend ${stat.positive ? 'positive' : 'negative'}`}>
                  {stat.positive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  <span>{stat.trend}</span>
                  <span className="trend-period">{t('dashboard.thisMonth')}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pending Actions */}
      <div className="pending-section">
        <h3>{t('dashboard.pendingActions')}</h3>
        <div className="pending-cards">
          <Link to="/offers?status=pending" className="pending-card">
            <div className="pending-icon offers">
              <Building2 size={20} />
            </div>
            <div className="pending-info">
              <span className="pending-count">{pendingItems.offers}</span>
              <span className="pending-label">{t('dashboard.pendingOffers')}</span>
            </div>
            <ArrowLeft size={18} className="pending-arrow" />
          </Link>

          <Link to="/users?status=pending" className="pending-card">
            <div className="pending-icon users">
              <Users size={20} />
            </div>
            <div className="pending-info">
              <span className="pending-count">{pendingItems.users}</span>
              <span className="pending-label">{t('dashboard.pendingUsers')}</span>
            </div>
            <ArrowLeft size={18} className="pending-arrow" />
          </Link>

          <Link to="/reports" className="pending-card">
            <div className="pending-icon reports">
              <AlertCircle size={20} />
            </div>
            <div className="pending-info">
              <span className="pending-count">{pendingItems.reports}</span>
              <span className="pending-label">{t('dashboard.pendingReports')}</span>
            </div>
            <ArrowLeft size={18} className="pending-arrow" />
          </Link>
        </div>
      </div>

      {/* Content Grid */}
      <div className="content-grid">
        {/* Chart Section */}
        <div className="data-section chart-section">
          <div className="section-header">
            <div className="section-title">
              <Activity size={20} />
              <span>{t('dashboard.growthChart')}</span>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1a5f4a" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#1a5f4a" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorOffers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#c4a35a" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#c4a35a" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf0" />
                <XAxis dataKey="name" stroke="#8a8a8a" fontSize={12} />
                <YAxis stroke="#8a8a8a" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    background: '#fff',
                    border: '1px solid #e8ecf0',
                    borderRadius: '12px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="users"
                  stroke="#1a5f4a"
                  strokeWidth={2}
                  fill="url(#colorUsers)"
                  name={t('dashboard.users')}
                />
                <Area
                  type="monotone"
                  dataKey="offers"
                  stroke="#c4a35a"
                  strokeWidth={2}
                  fill="url(#colorOffers)"
                  name={t('dashboard.offers')}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="data-section">
          <div className="section-header">
            <div className="section-title">
              <Clock size={20} />
              <span>{t('dashboard.recentActivity')}</span>
            </div>
            <Link to="/reports" className="view-all-link">
              {t('common.viewAll')}
            </Link>
          </div>
          <div className="activity-list">
            {recentActivities.map((activity) => {
              const Icon = activity.icon;
              return (
                <div key={activity.id} className="activity-item">
                  <div
                    className="activity-icon"
                    style={{ background: `${activity.color}15` }}
                  >
                    <Icon size={18} style={{ color: activity.color }} />
                  </div>
                  <div className="activity-content">
                    <span className="activity-title">{activity.title}</span>
                    <p className="activity-description">{activity.description}</p>
                    <span className="activity-time">
                      {formatRelativeTime(activity.time)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
